/* eslint-disable react/jsx-props-no-spreading */
import { useUser } from '@folklore/auth';
import { useForm } from '@folklore/forms';
import { useUrlGenerator } from '@folklore/routes';
import { faCheck, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';

import useSetUser from '../../hooks/useSetUser';
import * as AppPropTypes from '../../lib/PropTypes';

import Button from '../buttons/Button';
import EmailField from '../fields/Email';
import Body from '../partials/Body';

import styles from '../../../styles/forms/email.module.scss';

const propTypes = {
    title: AppPropTypes.message,
    description: AppPropTypes.message,
    placeholder: AppPropTypes.message,
    data: PropTypes.shape({
    }),
    className: PropTypes.string,
    titleClassName: PropTypes.string,
    descriptionClassName: PropTypes.string,
    inputClassName: PropTypes.string,
    inputContainerClassName: PropTypes.string,
    submitClassName: PropTypes.string,
    onSuccess: PropTypes.func,
};

const defaultProps = {
    title: null,
    description: null,
    placeholder: null,
    data: null,
    className: null,
    titleClassName: null,
    descriptionClassName: null,
    inputClassName: null,
    inputContainerClassName: null,
    submitClassName: null,
    onSuccess: null,
};

function EmailForm({
    title,
    description,
    placeholder,
    data,
    className,
    titleClassName,
    descriptionClassName,
    inputClassName,
    inputContainerClassName,
    submitClassName,
    onSuccess,
}) {
    const route = useUrlGenerator();
    const action = route('account.update_email');
    const user = useUser();
    const { email = null } = user || {};
    const hasEmail = email !== null;
    const setUser = useSetUser();

    const onComplete = useCallback(
        (newUser) => {
            setUser(newUser);
            if (onSuccess !== null) {
                onSuccess(newUser);
            }
        },
        [setUser, onSuccess],
    );

    const initialValue = useMemo(() => ({ email, data }), [email, data]);

    const {
        fields,
        onSubmit,
        errors,
        loading,
        generalError = null,
    } = useForm({
        initialValue,
        fields: ['email'],
        action,
        onComplete,
    });

    const { email: emailErrors = null } = errors || {};
    const intl = useIntl();

    return (
        <form
            className={classNames([
                styles.container,
                {
                    [className]: className !== null,
                    [styles.loading]: loading,
                    [styles.hasEmail]: hasEmail,
                },
            ])}
            onSubmit={onSubmit}
            action={action}
        >
            {title !== null ? (
                <h2
                    className={classNames([
                        styles.title,
                        {
                            [titleClassName]: titleClassName !== null,
                        },
                    ])}
                >
                    {title}
                </h2>
            ) : null}
            <div
                className={classNames([
                    styles.pill,
                    {
                        [inputContainerClassName]: inputContainerClassName !== null,
                    },
                ])}
            >
                <EmailField
                    className={classNames([
                        styles.input,
                        { [inputClassName]: inputClassName !== null },
                    ])}
                    {...fields.email}
                    placeholder={
                        placeholder !== null
                            ? placeholder
                            : intl.formatMessage({
                                  defaultMessage: 'Email',
                                  description: 'Field placeholder',
                              })
                    }
                    // required
                />
                <Button
                    className={classNames([
                        styles.submit,
                        { [submitClassName]: submitClassName !== null },
                    ])}
                    type="submit"
                >
                    <FontAwesomeIcon icon={hasEmail ? faCheck : faChevronRight} />
                </Button>
            </div>
            {description !== null ? (
                <Body
                    className={classNames([
                        styles.description,
                        { [descriptionClassName]: descriptionClassName !== null },
                    ])}
                    body={description}
                />
            ) : null}
            {emailErrors !== null ? (
                <div className={styles.errors}>
                    {(emailErrors || []).map((error, errorIndex) => (
                        <div key={`error-${errorIndex + 1}`}>{error}</div>
                    ))}
                    {generalError !== null ? <div>{generalError}</div> : null}
                </div>
            ) : null}
        </form>
    );
}

EmailForm.propTypes = propTypes;
EmailForm.defaultProps = defaultProps;

export default EmailForm;
